import { ReferencedEntry } from 'contentful';
import Handlebars from 'handlebars';
import React, { useMemo } from 'react';
import { useDCSText } from 'src/hooks/useDCSText';

import { getField } from 'src/utils/EntryUtil';

import { applyEntryDataAttributes } from '../EntryWrapper/EntryDataWrapper';
import { IEntryWrapperProps } from '../EntryWrapper/IEntryWrapperProps';
import { IMarkdownWrapperProps, MarkdownWrapper } from '../MarkdownWrapper/MarkdownWrapper';
import { useShopTemplateContext } from '../ShopTemplateContext/ShopTemplateContext';
import { IFieldWrapperProps } from './IFieldWrapperProps';

export interface IBlurb<T, E extends HTMLElement = HTMLDivElement, O = {}> extends IFieldWrapperProps<T, E> {
  templateData?: O;
}

export function Blurb<T, E extends HTMLElement = HTMLDivElement, O = {}>({
  entry,
  field,
  templateData: extraData,
  ...props
}: (IBlurb<T, E, O> & IMarkdownWrapperProps<E>)) {
  const source = field && getField(entry, field) as string | undefined;

  if (!source) return null;

  const dcsText = useDCSText(source);
  const shopTemplate = useShopTemplateContext();

  const template = useMemo(() => Handlebars.compile(dcsText), [ dcsText ]);
  const text = useMemo(() => template({ shop: shopTemplate, ...extraData }), [ template, shopTemplate, extraData]);

  const Wrapped = React.useCallback(applyEntryDataAttributes<
    T,
    ReferencedEntry<T>,
    IMarkdownWrapperProps<E> & IEntryWrapperProps<T, ReferencedEntry<T>>
  >(MarkdownWrapper), [ MarkdownWrapper ]);

  return (
    <Wrapped
      entry={entry}
      field={field}
      source={text}
      {...props}
    />
  );
}

Blurb.defaultProps = {
  tagName: 'div',
  templateData: {},
};
